import { useMemo, useState } from "react";

import { $enum } from "ts-enum-util";

import { PlanningDay, type Planning, type PlanningType } from "@eisox/api/core-api";
import { ActionButton, Button, Dialog, Radio, Select } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { CalendarIcon } from "@eisox/icons";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";

import { Help } from "~/components/help";
import { m } from "~/paraglide/messages";

import {
  getHoursEnd,
  getHoursStart,
  getNextDay,
  merge,
  planningTypeKeyMessages,
  sameDay,
  TargetDays,
  targetDaysToDays,
  updateSchema,
  type UpdateSchemaType,
} from "../helpers";

interface UpdatePlanningDialogProps extends Dialog.RootProps {
  value?: Planning;
  onValueChange?: (value: Planning[]) => Promise<void>;
  userDefinedPlannings: Planning[];
  types: PlanningType[];
}

export const UpdatePlanningDialog: React.FC<UpdatePlanningDialogProps> = ({
  value,
  onValueChange,
  userDefinedPlannings,
  types,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen = false,
  children,
  ...props
}) => {
  if (userDefinedPlannings.some(p => !p.userDefined)) throw new Error("userDefinedPlannings must be user defined");

  const [open, setOpen] = useControllableState({ prop: openProp, onChange: onOpenChange, defaultProp: defaultOpen });
  const [targetDays, setTargetDays] = useState<TargetDays>(TargetDays.MondayToFriday);

  const update = !!value;

  const otherPlannings = useMemo(
    () =>
      userDefinedPlannings.filter(
        p => !(p.day === value?.day && p.hourStart === value.hourStart && p.hourEnd === value.hourEnd),
      ),
    [userDefinedPlannings, value],
  );

  const defaultValues = useMemo(() => {
    let endDay = value?.day;
    if (value?.hourStart === "00:00" && value.hourEnd === "00:00") {
      endDay = getNextDay(value.day);
    }

    return {
      begin: { day: value?.day, hour: value?.hourStart },
      end: { day: endDay, hour: value?.hourEnd },
      type: value?.type,
      copyToOtherDays: false,
      targetDays: targetDaysToDays[targetDays],
    };
  }, [value, targetDays]);

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: updateSchema,
    },
    onSubmit: async ({ value }) => {
      await onValueChange?.(merge(value as UpdateSchemaType, otherPlannings));
      handleOpenChange(false);
    },
  });

  const begin = useSelector(form.store, state => state.values.begin);
  const end = useSelector(form.store, state => state.values.end);
  const copyToOtherDays = useSelector(form.store, state => state.values.copyToOtherDays);

  const daysOptions = $enum(PlanningDay)
    .getValues()
    .map(day => ({ name: m[day](), value: day }));

  const targetDaysOptions = [
    { name: `${m.monday()} - ${m.friday()}`, value: TargetDays.MondayToFriday },
    { name: `${m.saturday()} - ${m.sunday()}`, value: TargetDays.SaturdayToSunday },
    { name: `${m.monday()} - ${m.sunday()}`, value: TargetDays.MondayToSunday },
    { name: m.personalized(), value: TargetDays.Custom },
  ];

  const startHourOptions = getHoursStart(end.hour, begin.day, end.day).map(hour => ({
    name: hour,
    value: hour,
  }));

  const endHoureOptions = getHoursEnd(begin.hour, begin.day, end.day).map(hour => ({
    name: hour,
    value: hour,
  }));

  const typesOptions = types.map(type => ({ value: type, name: planningTypeKeyMessages[type] }));

  const isSameDay = sameDay({ begin, end });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    form.reset();
  };

  const handleChangeCopyToOtherDays = (value: boolean) => {
    form.setFieldValue("copyToOtherDays", value);
  };

  const handleChangeTargetDays = (value: string | string[]) => {
    setTargetDays(value as TargetDays);
    form.setFieldValue("targetDays", targetDaysToDays[value as TargetDays]);
  };

  const handleDeletePlanning = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await onValueChange?.(otherPlannings);
    handleOpenChange(false);
  };

  const handleEntireDay = () => {
    const currentBeginDay = form.getFieldValue("begin.day");
    if (!currentBeginDay) return;
    const nextDay = getNextDay(currentBeginDay);
    form.setFieldValue("begin.hour", "00:00");
    form.setFieldValue("end.day", nextDay);
    form.setFieldValue("end.hour", "00:00");
  };

  return (
    <Dialog.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger className="cursor-pointer" asChild>
        {children}
      </Dialog.Trigger>
      <Dialog.Content
        className="max-w-[min(570px,calc(100dvw-20px))]"
        title={update ? m.edit_time_slot() : m.add_time_slot()}
        icon={<CalendarIcon />}
      >
        <form
          className="flex w-full flex-col gap-[20px] overflow-y-auto text-base"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="sm:grid-flow-col sm:grid-rows-[auto_auto] grid grid-flow-row grid-cols-1 gap-[10px]">
              <form.AppField name="begin.day">
                {field => (
                  <field.Select
                    classNames={{ control: "w-full max-w-full md:w-[240px]" }}
                    label={m.begin()}
                    placeholder={m.select()}
                    multiple={false}
                    options={daysOptions}
                    searchInput={false}
                  />
                )}
              </form.AppField>
              <form.AppField name="begin.hour">
                {field => (
                  <field.Select
                    classNames={{ control: "w-full max-w-full md:w-[240px]" }}
                    placeholder={m.select()}
                    multiple={false}
                    options={startHourOptions}
                    searchInput={false}
                  />
                )}
              </form.AppField>
              <form.AppField name="end.day">
                {field => (
                  <field.Select
                    classNames={{ control: "w-full max-w-full md:w-[240px]" }}
                    label={m.end()}
                    placeholder={m.select()}
                    multiple={false}
                    options={daysOptions}
                    disabled={begin.day === undefined || begin.hour === undefined}
                  />
                )}
              </form.AppField>
              <form.AppField name="end.hour">
                {field => (
                  <field.Select
                    classNames={{ control: "w-full max-w-full md:w-[240px]" }}
                    placeholder={m.select()}
                    multiple={false}
                    options={endHoureOptions}
                    disabled={begin.day === undefined || begin.hour === undefined}
                    searchInput={false}
                  />
                )}
              </form.AppField>
            </div>
            <Button disabled={!begin.day} onClick={handleEntireDay}>
              {m.entire_day()}
            </Button>
            <form.AppField name="type">
              {field => (
                <field.Select
                  classNames={{ control: "w-full max-w-full md:w-[240px]" }}
                  label={m.type()}
                  placeholder={m.select()}
                  multiple={false}
                  options={typesOptions}
                />
              )}
            </form.AppField>
            <Collapsible.Root open={copyToOtherDays} onOpenChange={handleChangeCopyToOtherDays} disabled={!isSameDay}>
              <Collapsible.Trigger className="flex cursor-pointer items-center gap-[10px] select-none [&[data-disabled]_span]:text-primary-dark-gray">
                <Radio multiple checked={copyToOtherDays} disabled={!isSameDay} />
                <span>{m.duplicate_to_other_days()}</span>
                <Help>{m.duplication_only_same_day()}</Help>
              </Collapsible.Trigger>
              <Collapsible.Content className="*:min-w-0 mt-[10px] flex flex-wrap items-center gap-[10px] *:flex-1">
                <Select.Root multiple={false} value={targetDays} onValueChange={handleChangeTargetDays}>
                  <Select.Trigger>
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.Viewport>
                      {targetDaysOptions.map(option => (
                        <Select.Item key={option.value} value={option.value}>
                          {option.name}
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
                {targetDays === TargetDays.Custom && (
                  <form.AppField name="targetDays">
                    {field => (
                      <field.Select
                        classNames={{ control: "w-full max-w-full md:w-[240px]" }}
                        placeholder={m.select()}
                        multiple
                        options={daysOptions.filter(d => d.value !== begin.day)}
                        searchInput={false}
                      />
                    )}
                  </form.AppField>
                )}
              </Collapsible.Content>
            </Collapsible.Root>
            <div className="gap-5 relative flex w-full items-center justify-center">
              <Dialog.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Dialog.Close>
              <form.SubmitButton label={m.add()} />
              {update && (
                <Button
                  className="right-0 absolute text-primary-red"
                  onClick={e => {
                    void handleDeletePlanning(e);
                  }}
                >
                  {m.delete()}
                </Button>
              )}
            </div>
          </form.AppForm>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
